import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SupportedLanguage, AVAILABLE_LANGUAGES, LANGUAGE_STORAGE_KEY, LanguageInfo } from './types';

interface LanguageContextValue {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  availableLanguages: LanguageInfo[];
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();

  const setLanguage = useCallback((lang: SupportedLanguage) => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    i18n.changeLanguage(lang);
  }, [i18n]);

  const value = useMemo(() => ({
    language: i18n.language as SupportedLanguage,
    setLanguage,
    availableLanguages: AVAILABLE_LANGUAGES,
  }), [i18n.language, setLanguage]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
